import { chakra } from '@chakra-ui/react';
import { useId, useMemo } from 'react';
import { Text } from '../primitives/Text';

/**
 * A dependency-free, theme-aware line/area chart for a single time series (§14 dashboard).
 * Follows {@link CostChart}'s inline-SVG approach — no chart library — but renders FLUID:
 * the plot scales to its container width via a normalized `viewBox` +
 * `preserveAspectRatio="none"`, while `vector-effect="non-scaling-stroke"` keeps the line
 * and gridline weights crisp at any width. Axis text is HTML (below the plot) so it never
 * distorts with the horizontal stretch.
 *
 * Theme-aware: gridlines inherit `currentColor` from a `text.secondary` wrapper (light/dark
 * without hard-coded neutrals); the series color is an explicit brand hue (default EHE blue),
 * legible in both modes. Accessible: `role="img"` + a summarising `aria-label`, visible
 * x-labels, and a peak caption — the trend never relies on color alone.
 */

export interface LineChartPoint {
  /** Short x-axis label for this point (e.g. `Jul 15`). */
  label: string;
  value: number;
}

export interface LineChartProps {
  data: LineChartPoint[];
  /** Plot height in px (the width is always 100% of the container). */
  height?: number;
  /** Series color (brand hue). Default = EHE brand blue. */
  color?: string;
  /** Format a value for the tooltip + peak caption. */
  formatValue?: (n: number) => string;
  /** Accessible summary of the whole series. */
  ariaLabel: string;
  /** Fill the area under the line (default true). */
  area?: boolean;
  'data-testid'?: string;
}

const VB_W = 100;
const VB_H = 100;
const PAD_TOP = 10; // viewBox units of headroom so the peak never clips

const Plot = chakra('svg', { base: { display: 'block', width: 'full' } });

const AxisRow = chakra('div', {
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '2',
    mt: '2',
  },
});

function defaultFormat(n: number): string {
  return n.toLocaleString();
}

/** Pick up to `max` evenly-spaced points to label on the x-axis (always first + last). */
function axisTicks<T>(items: T[], max = 6): { item: T; index: number }[] {
  const n = items.length;
  if (n <= max) return items.map((item, index) => ({ item, index }));
  const step = (n - 1) / (max - 1);
  const out: { item: T; index: number }[] = [];
  for (let i = 0; i < max; i++) {
    const index = Math.round(i * step);
    out.push({ item: items[index]!, index });
  }
  return out;
}

export function LineChart({
  data,
  height = 180,
  color = '#005ff3',
  formatValue = defaultFormat,
  ariaLabel,
  area = true,
  'data-testid': testid,
}: LineChartProps) {
  const gradId = useId();

  const model = useMemo(() => {
    const n = data.length;
    const max = Math.max(1, ...data.map((d) => d.value));
    const plotH = VB_H - PAD_TOP;
    const xOf = (i: number) => (n <= 1 ? VB_W / 2 : (i / (n - 1)) * VB_W);
    const yOf = (v: number) => VB_H - (v / max) * plotH;

    const pts = data.map((d, i) => ({ x: xOf(i), y: yOf(d.value), ...d }));
    // A single point renders as a flat line across the plot so the card never looks broken.
    const line =
      n === 1
        ? `M 0,${pts[0]!.y} L ${VB_W},${pts[0]!.y}`
        : pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
    const fill =
      n === 1
        ? `M 0,${pts[0]!.y} L ${VB_W},${pts[0]!.y} L ${VB_W},${VB_H} L 0,${VB_H} Z`
        : `M ${pts[0]!.x},${VB_H} ` +
          pts.map((p) => `L ${p.x},${p.y}`).join(' ') +
          ` L ${pts[pts.length - 1]!.x},${VB_H} Z`;

    let peak = data[0] ?? null;
    for (const d of data) if (peak && d.value > peak.value) peak = d;
    const total = data.reduce((s, d) => s + d.value, 0);

    return { pts, line, fill, peak, total, bandW: n > 0 ? VB_W / n : VB_W };
  }, [data]);

  const isEmpty = data.length === 0 || model.total === 0;

  return (
    <div data-testid={testid}>
      {/* text.secondary wrapper → SVG currentColor is theme-aware for gridlines/baseline. */}
      <Text as="div" tone="secondary">
        <Plot
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="none"
          height={`${height}px`}
          role="img"
          aria-label={ariaLabel}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          {/* Gridlines at 0 / 50 / 100% — non-scaling stroke keeps them hairline at any width. */}
          {[PAD_TOP, (VB_H + PAD_TOP) / 2, VB_H].map((y, i) => (
            <line
              key={i}
              x1={0}
              x2={VB_W}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.14}
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {area && !isEmpty && <path d={model.fill} fill={`url(#${gradId})`} stroke="none" />}
          {!isEmpty && (
            <path
              d={model.line}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {/* Invisible hover bands give native tooltips per day without distorting geometry. */}
          {!isEmpty &&
            model.pts.map((p, i) => (
              <rect
                key={i}
                x={p.x - model.bandW / 2}
                y={0}
                width={model.bandW}
                height={VB_H}
                fill="transparent"
              >
                <title>{`${p.label}: ${formatValue(p.value)}`}</title>
              </rect>
            ))}
        </Plot>
      </Text>

      {data.length > 0 && (
        <AxisRow>
          {axisTicks(data).map(({ item, index }) => (
            <Text key={index} as="span" size="xs" tone="muted">
              {item.label}
            </Text>
          ))}
        </AxisRow>
      )}

      {!isEmpty && model.peak && (
        <Text as="div" size="xs" tone="muted" style={{ marginTop: '0.5rem' }}>
          Peak {formatValue(model.peak.value)} on {model.peak.label}
        </Text>
      )}
    </div>
  );
}
